import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import TextField from '@amf/shared/components/form/TextField';
import { SeasonLeagueFragment, TeamListItemFragment } from '@amf/shared/types/graphql';
import SelectField from '@amf/shared/components/form/SelectField';
import { MatchSortOrder } from '@amf/shared/data/hooks/useMatches';

const schema = yup.object({
  dateFrom: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  dateTo: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  team: yup.string(),
  phase: yup.string(),
  round: yup.number(),
  sortBy: yup.string(),
});

export type LeagueMatchesFilterData = {
  dateFrom: string | null;
  dateTo: string | null;
  team: string | null;
  phase: string;
  round: string | null;
  sortBy: MatchSortOrder | null;
};

const defaultValues: LeagueMatchesFilterData = {
  dateFrom: null,
  dateTo: null,
  team: '',
  phase: '',
  round: '',
  sortBy: 'round',
};

interface LeagueMatchesFilterProps {
  league: SeasonLeagueFragment;
  teams: TeamListItemFragment[];
  onChange?: (data: LeagueMatchesFilterData) => void;
}

function LeagueMatchesFilter({ league, teams, onChange }: LeagueMatchesFilterProps) {
  const { t } = useTranslate();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<LeagueMatchesFilterData>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      phase: league.phases[0]?.id,
    },
  });

  useEffect(() => {
    setValue('dateFrom', null);
    setValue('dateTo', null);
    setValue('team', '');
    const phase = league.phases.find(phase => phase.current) ?? league.phases[0];
    setValue('phase', phase?.id);
    setValue('round', '');
    setValue('sortBy', 'round');
  }, [league, setValue]);

  const update = useCallback(
    (data: LeagueMatchesFilterData) => {
      if (!onChange) return;
      onChange(data);
    },
    [onChange],
  );

  watch(update);

  return (
    <div className='Filter'>
      <form onSubmit={handleSubmit(update)} className='Filter__form'>
        <Controller
          name='phase'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.phase')}
              error={errors.phase?.message}
              {...field}
            >
              {league.phases.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name='team'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.team')}
              error={errors.team?.message}
              {...field}
            >
              <option value={''}>{t('filter.team.empty')}</option>
              {teams.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name='round'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.round')}
              error={errors.round?.message}
              {...field}
            >
              <option value={''}>{t('filter.round.empty')}</option>
              {[...Array(league.rounds)].map((_, round) => (
                <option key={round + 1} value={round + 1}>
                  {t('filter.round.item', { round: round + 1 })}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name='dateFrom'
          control={control}
          render={({ field }) => (
            <TextField
              className='Filter__field'
              label={t('filter.dateFrom')}
              type='date'
              error={errors.dateFrom?.message}
              {...field}
            />
          )}
        />
        <Controller
          name='dateTo'
          control={control}
          render={({ field }) => (
            <TextField
              className='Filter__field'
              label={t('filter.dateTo')}
              type='date'
              error={errors.dateTo?.message}
              {...field}
            />
          )}
        />
        <Controller
          name='sortBy'
          control={control}
          render={({ field }) => (
            <SelectField
              className='Filter__field'
              label={t('filter.sortBy')}
              error={errors.sortBy?.message}
              {...field}
            >
              <option value='round'>{t('filter.sortBy.round')}</option>
              <option value='time'>{t('filter.sortBy.time')}</option>
            </SelectField>
          )}
        />
      </form>
    </div>
  );
}

export default LeagueMatchesFilter;
